import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Row, Col, Card, Spinner, Pagination } from 'react-bootstrap';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/i18n';
import PublicPageLayout from './PublicPageLayout';
import { apiUrl, assetUrl } from '../utils/apiUrl';

const PAGE_SIZE = 24;

function getPostUrl(post) {
  if (!post?.slug) return '#';
  const lang = post.language === 'en' ? 'en' : 'es';
  const base = lang === 'en' ? '/news' : '/noticias';
  const paths = { en: { movie: 'movies', series: 'series', music: 'music' }, es: { movie: 'peliculas', series: 'series', music: 'musica' } };
  const categoryPath = paths[lang][post.category] || paths[lang].movie;
  return `${base}/${categoryPath}/${post.slug}`;
}

const AllNews = () => {
  const { language } = useLanguage();
  const lang = language === 'en' ? 'en' : 'es';
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const offset = (page - 1) * PAGE_SIZE;
    fetch(apiUrl(`/api/blog/posts?language=${lang}&limit=${PAGE_SIZE}&offset=${offset}`), { credentials: 'include' })
      .then((r) => r.ok ? r.json() : { posts: [], total: 0 })
      .then((data) => {
        if (!cancelled) {
          setPosts(Array.isArray(data.posts) ? data.posts : []);
          setTotal(data.total != null ? data.total : 0);
        }
      })
      .catch(() => { if (!cancelled) { setPosts([]); setTotal(0); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [lang, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <PublicPageLayout
      title={t('allNews.title', language)}
      subtitle={t('allNews.subtitle', language)}
      backTo="/"
    >
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">{t('allNews.loading', language)}</p>
        </div>
      ) : posts.length === 0 ? (
        <p className="text-center text-muted py-5">{t('allNews.noPosts', language)}</p>
      ) : (
        <>
          <Row xs={1} md={2} lg={3} className="g-4">
            {posts.map((post) => (
              <Col key={post.id}>
                <Link to={getPostUrl(post)} className="text-decoration-none text-dark d-block h-100">
                  <Card className="h-100 miskosas-card border-0 overflow-hidden">
                    {post.featured_image ? (
                      <div style={{ height: 200, overflow: 'hidden' }}>
                        <img
                          src={assetUrl(post.featured_image)}
                          alt=""
                          className="w-100 h-100"
                          style={{ objectFit: 'cover' }}
                        />
                      </div>
                    ) : (
                      <div className="bg-secondary d-flex align-items-center justify-content-center" style={{ height: 200 }}>
                        <span className="display-4 text-white opacity-50">📰</span>
                      </div>
                    )}
                    <Card.Body>
                      <Card.Title className="fw-bold small mb-2 line-clamp-2" style={{ minHeight: '2.5em' }}>
                        {post.title}
                      </Card.Title>
                      <p className="text-muted small mb-0 line-clamp-3">{post.excerpt || post.title}</p>
                      <span className="text-primary small mt-2 d-inline-block">{t('allNews.readMore', language)} →</span>
                    </Card.Body>
                  </Card>
                </Link>
              </Col>
            ))}
          </Row>

          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-4 pt-4">
              <Pagination>
                <Pagination.Prev
                  disabled={page <= 1}
                  onClick={(e) => { e?.preventDefault?.(); setPage((p) => Math.max(1, p - 1)); }}
                />
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) pageNum = i + 1;
                  else if (page <= 3) pageNum = i + 1;
                  else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                  else pageNum = page - 2 + i;
                  return (
                    <Pagination.Item
                      key={pageNum}
                      active={pageNum === page}
                      onClick={(e) => { e?.preventDefault?.(); setPage(pageNum); }}
                    >
                      {pageNum}
                    </Pagination.Item>
                  );
                })}
                <Pagination.Next
                  disabled={page >= totalPages}
                  onClick={(e) => { e?.preventDefault?.(); setPage((p) => p + 1); }}
                />
              </Pagination>
            </div>
          )}
        </>
      )}
    </PublicPageLayout>
  );
};

export default AllNews;
